import React, { useCallback, useEffect, useState } from "react";
import { Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { AdminHeader, borderStyle } from "../ui";
import SmartImage from "../../components/SmartImage";
import { PageLoader } from "../../components/States";

export default function MediaAdmin() {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/admin/media", { params: { search: q || undefined, limit: 200 } })
      .then((r) => { setRows(r.data.media); setTotal(r.data.total); })
      .catch(() => setRows([]));
  }, [q]);

  useEffect(() => { load(); }, [load]);

  async function upload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await adminApi.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.deduped ? "Already in library" : "Uploaded");
      load();
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail });
    } finally {
      setBusy(false);
    }
  }

  async function remove(m) {
    const { data } = await adminApi.get(`/admin/media/${m.id}/usage`);
    const msg = data.total > 0
      ? `This image is used in ${data.total} place(s). Delete anyway?`
      : "Delete this image?";
    if (!window.confirm(msg)) return;
    await adminApi.delete(`/admin/media/${m.id}`);
    toast.success("Deleted");
    load();
  }

  return (
    <div data-testid="admin-media">
      <AdminHeader title="Media library" subtitle={`${total} images stored locally in this project`}>
        <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5" style={borderStyle}>
          <Search className="h-4 w-4 gold-text" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search filename" className="bg-transparent text-sm outline-none" data-testid="admin-media-search" />
        </div>
        <label className="btn-gold cursor-pointer" data-testid="admin-media-upload">
          <Upload className="h-4 w-4" /> {busy ? "Uploading..." : "Upload image"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </AdminHeader>

      {!rows ? <PageLoader /> : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {rows.map((m) => (
            <div key={m.id} className="card-nq" data-testid="admin-media-item">
              <SmartImage src={m.url} alt={m.alt} ratio="aspect-square" fit="contain" />
              <div className="p-2">
                <p className="truncate text-xs" title={m.original_name}>{m.original_name}</p>
                <p className="mt-0.5 font-mono text-[10px] text-[color:var(--nq-stone)]">
                  {(m.bytes / 1024).toFixed(0)} KB
                </p>
                <button type="button" onClick={() => remove(m)} className="mt-1.5 flex items-center gap-1 text-[10px] text-[color:var(--nq-stone)] hover:text-[#e07a72]" data-testid="admin-media-delete">
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
